/**
 * Buyer Intelligence product page — BP-022.
 * Rank verified cash buyers by fit + funding + track record.
 */
import { Users2, Wallet, ClipboardCheck, Filter, Zap, Mail } from "lucide-react";
import ProductPageTemplate, { ProductScreen } from "../ProductPageTemplate";

const heroMockup = (
  <ProductScreen title="buyers / matches · 42 Coach Test Ln">
    <div className="space-y-2 text-xs">
      {[
        ["Meridian Capital", 94, "$1.2M", "Verified · 8 closes"],
        ["North Shore Homes", 91, "$800k", "Verified · 5 closes"],
        ["Great Lakes REI", 88, "$600k", "Verified · 12 closes"],
        ["First-Time Flipper LLC", 62, "$180k", "Unverified"],
      ].map(([name, fit, budget, meta]) => (
        <div key={name} className="flex items-center gap-2 border-b border-slate-100 pb-2">
          <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
            {String(fit)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 truncate">{name}</div>
            <div className="text-[10px] text-slate-500">{meta}</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-slate-900">{budget}</div>
            <div className="text-[9px] text-emerald-600">funding OK</div>
          </div>
        </div>
      ))}
    </div>
  </ProductScreen>
);

export default function BuyerIntelligencePage() {
  return (
    <ProductPageTemplate
      slug="buyer-intelligence"
      bp="BP-022 · Buyer Intelligence"
      eyebrow="Product · Buyer Intelligence"
      title="Not just a buyers list."
      titleAccent="A ranked, verified network."
      subtitle="Every cash buyer in your database is enriched with proof of funds, criteria fit, close history, and engagement signals — so the top 3 matches for any property surface automatically, ranked by likelihood to close."
      bullets={[
        "0–100 Fit Score per buyer, per property",
        "Verified funding status via bank statement upload or ACH proof",
        "Track record captured on the timeline — closes, backouts, price drops",
      ]}
      heroMockup={heroMockup}
      capabilities={[
        { icon: Users2, title: "Fit Ranking", desc: "Property → buyers matcher scores each buyer 0–100 on strategy, geography, price band, and asset class fit." },
        { icon: Wallet, title: "Funding Verification", desc: "Upload bank statements or link ACH — verified buyers get priority routing on every deal." },
        { icon: ClipboardCheck, title: "Track Record", desc: "Every close, backout, and negotiation captured. Reputation scores are earned, not claimed." },
        { icon: Filter, title: "Smart Filters", desc: "Filter by strategy (BRRRR-only, novation-friendly, subject-to buyers) — save filters as recurring lists." },
        { icon: Zap, title: "Instant Outreach", desc: "Blast the top-N matched buyers with property + evidence panel in one click. TCPA-compliant." },
        { icon: Mail, title: "Engagement Signals", desc: "Track email opens, offer submissions, follow-ups — feed engagement back into the Fit Score." },
      ]}
      howItWorks={[
        { step: 1, title: "Enrich", desc: "Each buyer's criteria + funding + history is captured or uploaded once." },
        { step: 2, title: "Match", desc: "Every new property runs against the buyer graph — top-N matches surface in seconds." },
        { step: 3, title: "Verify", desc: "Only funded, verified buyers get routed first. Unverified are held for warm-up." },
        { step: 4, title: "Close", desc: "Signed docs and wired funds close the loop and update the buyer's track record." },
      ]}
      integrations={[
        { bp: "BP-022", name: "Buyer Intelligence" },
        { bp: "BP-018", name: "Buyer Matching" },
        { bp: "BP-014", name: "Strategy Comparison" },
        { bp: "BP-024", name: "Market Intelligence" },
        { bp: "BP-002", name: "Property Workspace" },
        { bp: "BP-025", name: "Coach Cites Buyers" },
        { bp: "BP-023", name: "Portfolio Overlay" },
        { bp: "BP-019", name: "Exec Dashboard" },
      ]}
      proof={{
        stat: "3.4×",
        label: "faster time-to-buyer-signature",
        quote: "We stopped emailing our entire list. Top-3 verified matches, instantly, with proof of funds — buyers respond faster because they know they're first.",
        attribution: "Disposition manager · 12-person team",
      }}
      related={[
        { title: "Property Workspace", to: "/products/property-workspace", desc: "See ranked buyers inside every workspace." },
        { title: "Strategy Comparison", to: "/products/strategy-comparison", desc: "Match buyers by strategy fit, automatically." },
        { title: "AI Acquisition Coach", to: "/products/ai-acquisition-coach", desc: "Coach cites buyer fit in every recommendation." },
      ]}
    />
  );
}
